import { Button, Modal, Radio, Stack, Text, Box, Flex, ScrollArea, Tooltip } from '@mantine/core';
import { MarkdownView } from 'src/common/components/markdown-editor/MarkdownView';
import React, { useEffect, useState } from 'react';
import { GenericErrorModal } from '../../../error/GenericErrorModal';
import PageLoader from '../../../UI/loader/PageLoader';
import { DataUploadModel } from 'src/data-capture/hooks/templates/useDataUploadModels';
import { IconFile, IconCheck, IconHelpCircle } from '@tabler/icons-react';
import { DateInput } from '@mantine/dates';
import { parseUKDate } from 'src/non-financial-data/components/fields/DateField';

export type FileWithTemplate<T> = {
  file: T;
  modelId: string;
  documentDate?: string | null;
};

type FileInfo = {
  name?: string;
  size?: number;
};

type Props<T extends FileInfo> = {
  opened: boolean;
  close: () => void;
  files: T[];
  dataUploadModels: DataUploadModel[];
  onSubmit: (fileWithTemplate: FileWithTemplate<T>[]) => Promise<void>;
  defaultDocumentDate?: string | null;
};

export function UploadFilesModal<T extends FileInfo>({
  opened,
  close,
  files,
  onSubmit,
  dataUploadModels = [],
  defaultDocumentDate,
}: Props<T>) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setIsError] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string>(dataUploadModels[0]?.id);
  const [documentDate, setDocumentDate] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDocumentDate(defaultDocumentDate ?? null);
    }
  }, [opened]);

  useEffect(() => {
    if (dataUploadModels.length > 0) {
      setSelectedModelId(dataUploadModels[0].id);
    }
  }, [dataUploadModels]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName: string, maxLength: number = 50): string => {
    if (fileName.length <= maxLength) return fileName;
    return fileName.substring(0, maxLength) + '...';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedModelId) {
      return;
    }

    setIsLoading(true);
    await onSubmit(
      files.map(file => ({
        file,
        modelId: selectedModelId,
        documentDate,
      })),
    );
    setIsLoading(false);
    close();
  };

  if (error) {
    return <GenericErrorModal opened={true} onClose={() => setIsError(false)} />;
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      size={'lg'}
      title={
        <Text size="xxl" fw={700}>
          {isLoading ? 'Uploading file' : 'What type of file is this?'}
        </Text>
      }
    >
      <form onSubmit={handleSubmit} data-testid="upload-files-modal">
        {isLoading ? (
          <PageLoader absolute={false} />
        ) : (
          <Stack gap="lg" mt="xxxs">
            <Radio.Group value={selectedModelId} onChange={setSelectedModelId}>
              <Stack gap="xs">
                {dataUploadModels.map(model => (
                  <Radio.Card
                    key={model.id}
                    value={model.id}
                    radius="md"
                    style={{
                      borderRadius: '16px',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      borderColor:
                        selectedModelId === model.id
                          ? 'var(--mantine-color-brand-6)'
                          : 'var(--mantine-color-gray-4)',
                      backgroundColor:
                        selectedModelId === model.id
                          ? 'var(--mantine-color-brand-0)'
                          : 'transparent',
                    }}
                  >
                    <Box p="sm">
                      <Flex align="center">
                        <Stack gap="xxs" style={{ flex: 1, marginRight: '28px' }}>
                          <Text fw={700} size="sm" c="gray.8">
                            {model.displayName}
                          </Text>
                          {model.description && (
                            <Box fz="xs" c="gray.8">
                              <MarkdownView value={model.description.replace(/\\n/g, '\n')} />
                            </Box>
                          )}
                        </Stack>
                        <Box style={{ width: '20px', height: '20px' }}>
                          {selectedModelId === model.id && (
                            <IconCheck size={20} color="var(--mantine-color-brand-6)" />
                          )}
                        </Box>
                      </Flex>
                    </Box>
                  </Radio.Card>
                ))}
              </Stack>
            </Radio.Group>

            <DateInput
              clearable
              label={
                <Flex direction={'row'} align={'center'}>
                  <Text c="gray.10" fw={600} size={'md'} mr={'xxxs'}>
                    Document date:
                  </Text>
                  <Tooltip
                    withArrow
                    multiline
                    w={200}
                    label={
                      'Setting the document date will give us more context when using it for feature like suitability report generation.'
                    }
                  >
                    <IconHelpCircle stroke={2} size={15} />
                  </Tooltip>
                </Flex>
              }
              labelProps={{ variant: 'light' }}
              placeholder="DD/MM/YYYY"
              valueFormat="D MMMM YYYY"
              dateParser={parseUKDate}
              value={documentDate ? new Date(documentDate) : null}
              onChange={date => setDocumentDate(date ? date.toString() : null)}
            />

            <Text c="gray.10" fw={600} size={'md'}>
              File(s) being processed:
            </Text>

            <ScrollArea
              h={files.length > 3 ? 225 : 'auto'}
              scrollbarSize={6}
              scrollHideDelay={1500}
              type="scroll"
              offsetScrollbars={false}
              style={{
                borderRadius: '16px',
                border: files.length > 3 ? '1px solid var(--mantine-color-gray-3)' : 'none',
                overflowX: 'hidden',
              }}
            >
              <Stack gap="xs" p={files.length > 3 ? 'xs' : 0}>
                {files.map((file, index) => (
                  <Box
                    key={index}
                    p="sm"
                    style={{
                      borderRadius: '16px',
                      backgroundColor: 'var(--mantine-color-gray-1)',
                    }}
                  >
                    <Flex align="center" justify="space-between" style={{ width: '100%' }}>
                      <Flex gap="xs" align="center">
                        <IconFile
                          size={20}
                          color="var(--mantine-color-gray-6)"
                          style={{ flexShrink: 0 }}
                        />
                        <Text fw={500} size="sm" c="gray.10" data-testid="file-name">
                          {truncateFileName(file.name || 'Unnamed file')}
                        </Text>
                      </Flex>
                      <Text fw={400} size="xs" c="gray.8" style={{ flexShrink: 0 }}>
                        {formatFileSize(file.size || 0)}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </Stack>
            </ScrollArea>

            <Button
              variant="black"
              type="submit"
              fullWidth
              size="xxl"
              data-autofocus
              disabled={!selectedModelId}
            >
              Submit
            </Button>
          </Stack>
        )}
      </form>
    </Modal>
  );
}
